import type {
  Completion,
  CompletionContext,
  CompletionResult,
  CompletionSource,
} from '@codemirror/autocomplete';
import {syntaxTree} from '@codemirror/language';
import type {EditorState} from '@codemirror/state';
import type {SyntaxNode} from '@lezer/common';
import {defineCSSCompletionSource} from '@codemirror/lang-css';
import type {CompletionData} from './types';
import {getClassCompletionsByText} from './html-source';
import {
  getCssPropertyCompletions,
  CSS_GLOBAL_VALUES,
} from './css-property-values';

/**
 * The built-in CM6 CSS completion source handles property names, pseudo-classes,
 * at-rules, tags, and variable names. We delegate to it for everything EXCEPT
 * property value completions (ValueName nodes and explicit activation after `:`)
 * where we provide spec-accurate, property-aware completions instead.
 */
// Keep built-in CSS variable completions; override ValueName with property-aware values
const builtinCssSource: CompletionSource = defineCSSCompletionSource(
  (n: {name: string}) => n.name === 'VariableName'
);

/**
 * Pre-built Completion objects for CSS global values (inherit, initial, etc.).
 * Allocated once at module load to avoid repeated allocations in the hot path.
 * boost: -1 sorts these after property-specific values (boost 0).
 */
const globalCompletions: readonly Completion[] = CSS_GLOBAL_VALUES.map(
  (label) => ({label, type: 'keyword', boost: -1})
);

/**
 * Cache: property name → merged completions array (property-specific + globals).
 * Avoids re-concatenating on every keystroke for the same property.
 */
const mergedCache = new Map<string, readonly Completion[]>();

function getMergedCompletions(property: string): readonly Completion[] | null {
  const cached = mergedCache.get(property);
  if (cached !== undefined) return cached;

  const propertyOptions = getCssPropertyCompletions(property);
  if (!propertyOptions) return null;

  // Concat once, cache forever — both arrays are immutable
  const merged = (propertyOptions as Completion[]).concat(
    globalCompletions as Completion[]
  );
  mergedCache.set(property, merged);
  return merged;
}

/**
 * Completion source for CSS contexts.
 *
 * Priority order:
 * 1. Webflow-specific: class selectors (`.foo`), CSS variables (`var(--x)`)
 * 2. Property-aware values: spec-derived valid keywords for the current property
 * 3. Built-in CM6: property names, pseudo-classes, at-rules, tags
 * 4. Fallback: class="" detection in mixed HTML/CSS contexts
 */
export function cssCompletionSource(
  context: CompletionContext,
  data: CompletionData | null
): CompletionResult | null {
  const {state, pos} = context;

  // Check for class selector: cursor after `.` in a selector context
  const classMatch = context.matchBefore(/\.[\w-]*/);
  if (classMatch && data) {
    return getClassSelectorCompletions(classMatch, data);
  }

  // Check for `var(` prefix — user is explicitly typing a variable reference.
  // Return variable completions if available, or null to suppress property
  // values (the user clearly intends to type a variable, not a keyword).
  const varPrefixMatch = context.matchBefore(/var\(-{0,2}[\w-]*/);
  if (varPrefixMatch) {
    if (data) {
      return getVarPrefixCompletions(varPrefixMatch, data);
    }
    return null;
  }

  // Property-value completions: detect property name from syntax tree
  // and suggest only valid values. Auto-triggers (no Ctrl+Space needed).
  if (isAfterColon(state, pos)) {
    const propertyName = getPropertyName(state, pos);
    if (propertyName) {
      const options = getMergedCompletions(propertyName);
      if (options) {
        const word = context.matchBefore(/[\w-]*/);
        const from = word?.from ?? pos;
        return {from, options, validFor: /^[\w-]*$/};
      }
    }

    // In value position but no property-specific completions available.
    // Return null — don't fall through to the built-in source which would
    // dump every CSS keyword regardless of property context.
    return null;
  }

  // Outside value position: delegate to built-in CM6 CSS completions for
  // property names, pseudo-classes, at-rules, and tag selectors.
  const builtinResult = builtinCssSource(context) as CompletionResult | null;
  if (builtinResult) return builtinResult;

  // Fallback: detect class="..." by raw text (HTML inside unclosed <style>)
  if (data) {
    return getClassCompletionsByText(context, data);
  }

  return null;
}

/**
 * Extract the CSS property name from the syntax tree at the given position.
 *
 * Handles two cases:
 * 1. Cursor inside a Declaration (e.g., "display: bl|") — walk up to Declaration.
 * 2. Cursor in Block right after a Declaration with no value yet (e.g., "display: |}")
 *    — the parser ends the Declaration at the colon, so the cursor lands in the Block.
 *    Use childBefore(pos) to find the preceding Declaration.
 *
 * Falls back to text extraction when the syntax tree doesn't produce a
 * Declaration (e.g., "display:" with no value or semicolon, which the CSS
 * parser misinterprets as a PseudoClassSelector).
 */
function getPropertyName(state: EditorState, pos: number): string | null {
  const tree = syntaxTree(state);
  let node: SyntaxNode | null = tree.resolveInner(pos, -1);

  // Walk up to find a Declaration or Block node (max 5 levels)
  let depth = 0;
  while (
    node &&
    node.name !== 'Declaration' &&
    node.name !== 'Block' &&
    depth < 5
  ) {
    node = node.parent;
    depth++;
  }

  if (node) {
    let decl: SyntaxNode | null = null;

    if (node.name === 'Declaration') {
      decl = node;
    } else if (node.name === 'Block') {
      // Cursor is in the Block — check if there's a Declaration just before
      const before = node.childBefore(pos);
      if (before?.name === 'Declaration') {
        decl = before;
      }
    }

    if (decl) {
      const propNode = decl.firstChild;
      if (propNode && propNode.name === 'PropertyName') {
        return state.doc.sliceString(propNode.from, propNode.to);
      }
    }
  }

  // Fallback: extract property name from text when the syntax tree fails.
  // This handles incomplete declarations (e.g., "display:" with no value
  // or semicolon) that the CSS parser misinterprets as pseudo-selectors.
  return getPropertyNameFromText(state, pos);
}

/**
 * Text-based extraction of the CSS property name before the cursor.
 * Looks backward from `pos` for a `property-name:` pattern, skipping
 * whitespace between the colon and the cursor.
 */
function getPropertyNameFromText(
  state: {doc: {sliceString: (from: number, to: number) => string}},
  pos: number
): string | null {
  const lineStart = Math.max(0, pos - 200);
  const textBefore = state.doc.sliceString(lineStart, pos);

  // Match "property-name:" followed by optional whitespace and an optional
  // partial value (what the user has typed so far). The property name is a
  // CSS identifier: letters, digits, and hyphens, not starting with a digit.
  const match = textBefore.match(/([\w-]+)\s*:\s*[\w-]*$/);
  if (!match) return null;

  const name = match[1]!;

  // Guard: reject names that start with a digit (not valid CSS properties)
  // or single-char names that are likely partial selectors (e.g., 'a', 'b' in '.ab-class')
  if (/^\d/.test(name) || name.length < 2) return null;

  return name;
}

/**
 * Heuristic: check whether `pos` is inside a property value (after `:`)
 * rather than in a selector or property name. Uses a 200-char lookback,
 * which covers virtually all single-property declarations. Multi-line
 * values exceeding 200 chars before the cursor will miss, but
 * `getPropertyName()` (syntax-tree based) is the primary detection path;
 * this is a lightweight secondary signal.
 */
function isAfterColon(
  state: {doc: {sliceString: (from: number, to: number) => string}},
  pos: number
): boolean {
  const lineStart = Math.max(0, pos - 200);
  const textBefore = state.doc.sliceString(lineStart, pos);
  const lastColon = textBefore.lastIndexOf(':');
  const lastBrace = Math.max(
    textBefore.lastIndexOf('{'),
    textBefore.lastIndexOf('}'),
    textBefore.lastIndexOf(';')
  );
  return lastColon > lastBrace;
}

function getClassSelectorCompletions(
  match: {from: number},
  data: CompletionData
): CompletionResult | null {
  const options = data.cssClasses.map((cls) => ({
    label: cls.className,
    displayLabel:
      cls.displayName !== cls.className ? cls.displayName : undefined,
    apply: cls.className,
    type: 'class' as const,
  }));

  if (options.length === 0) return null;

  return {
    from: match.from + 1,
    options,
    validFor: /^[\w-]*$/,
  };
}

/**
 * User has typed `var(`, `var(-`, `var(--`, or `var(--partial`.
 * Replace the entire match with `var(--name)`.
 */
function getVarPrefixCompletions(
  match: {from: number; text: string},
  data: CompletionData
): CompletionResult | null {
  if (data.cssVariables.length === 0) return null;

  return {
    from: match.from,
    options: data.cssVariables.map((v) => ({
      label: `var(--${v.cssName})`,
      displayLabel: v.displayName,
      type: 'variable' as const,
      detail: v.preview || undefined,
      apply: (view: any, completion: any, from: number, to: number) => {
        const insert = `var(--${v.cssName})`;
        const charAfter = view.state.doc.sliceString(to, to + 1);
        const adjustedTo = charAfter === ')' ? to + 1 : to;
        view.dispatch({
          changes: {from, to: adjustedTo, insert},
          selection: {anchor: from + insert.length},
        });
      },
    })),
    validFor: /^var\(-{0,2}[\w-]*\)?$/,
  };
}
